/* Fellora theme helpers: read more, sticky add-to-cart, carousels, hero slider, offer popup, delivery estimate. */
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Read more ---------- */
  if (!customElements.get('fl-read-more')) {
    customElements.define(
      'fl-read-more',
      class extends HTMLElement {
        connectedCallback() {
          this.content = this.querySelector('.fl-readmore__content');
          this.toggle = this.querySelector('.fl-readmore__toggle');
          if (!this.content || !this.toggle) return;
          this.limit = parseInt(this.dataset.collapsedHeight || '240', 10);
          this.toggle.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')));
          requestAnimationFrame(() => this.measure());
        }
        measure() {
          if (this.content.scrollHeight > this.limit + 60) {
            this.classList.add('is-clamped');
            this.content.style.maxHeight = this.limit + 'px';
            this.toggle.hidden = false;
          }
        }
        setOpen(open) {
          this.classList.toggle('is-open', open);
          this.toggle.setAttribute('aria-expanded', String(open));
          this.content.style.maxHeight = open ? this.content.scrollHeight + 'px' : this.limit + 'px';
          if (!open) this.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    );
  }

  /* ---------- Sticky add-to-cart (mobile) ---------- */
  if (!customElements.get('fl-sticky-atc')) {
    customElements.define(
      'fl-sticky-atc',
      class extends HTMLElement {
        connectedCallback() {
          if (this.closest('quick-add-modal')) return;
          const section = this.dataset.section;
          this.mainButton = document.getElementById(`ProductSubmitButton-${section}`);
          this.buyZone = this.closest('product-info')?.querySelector('.fl-zone--buy');
          this.priceEl = document.getElementById(`price-${section}`);
          this.button = this.querySelector('.fl-sticky-atc__button');
          this.priceOut = this.querySelector('[data-fl-sticky-price]');
          if (!this.mainButton || !this.buyZone) return;

          this.button.addEventListener('click', () => {
            if (this.mainButton.disabled) {
              this.buyZone.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
              return;
            }
            this.mainButton.click();
          });

          // Show the bar whenever the buy box is out of view: below the fold on first load
          // (phone ad visitors land on the gallery) or after it has scrolled past.
          let ticking = false;
          const update = () => {
            ticking = false;
            const zone = this.buyZone.getBoundingClientRect();
            const button = this.mainButton.getBoundingClientRect();
            const passed = zone.bottom < 0 || button.bottom > window.innerHeight;
            if (passed === this.classList.contains('is-visible')) return;
            this.classList.toggle('is-visible', passed);
            this.setAttribute('aria-hidden', String(!passed));
            this.button.tabIndex = passed ? 0 : -1;
            document.body.classList.toggle('fl-has-sticky-atc', passed);
          };
          const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
          };
          window.addEventListener('scroll', onScroll, { passive: true });
          window.addEventListener('resize', onScroll, { passive: true });
          update();

          this.sync();
          new MutationObserver(() => this.sync()).observe(this.mainButton, { attributes: true, childList: true, subtree: true });
          if (this.priceEl) new MutationObserver(() => this.sync()).observe(this.priceEl, { childList: true, subtree: true });
        }
        sync() {
          // Re-query: the theme swaps these nodes when the variant changes.
          const section = this.dataset.section;
          this.mainButton = document.getElementById(`ProductSubmitButton-${section}`) || this.mainButton;
          const label = this.mainButton.querySelector('span')?.textContent.trim();
          this.button.textContent = label || this.button.textContent;
          this.button.classList.toggle('is-disabled', this.mainButton.disabled);
          const price = this.priceEl?.querySelector('.price');
          if (price && this.priceOut) {
            const sel = price.classList.contains('price--on-sale')
              ? '.price__sale .price-item--sale'
              : '.price__regular .price-item--regular:last-of-type';
            const text = price.querySelector(sel)?.textContent.trim();
            if (text) this.priceOut.textContent = text;
          }
        }
      }
    );
  }

  /* ---------- Frequently bought together ---------- */
  if (!customElements.get('fl-bought-together')) {
    customElements.define(
      'fl-bought-together',
      class extends HTMLElement {
        connectedCallback() {
          if (this.closest('quick-add-modal')) return;
          this.section = this.dataset.section;
          this.button = this.querySelector('[data-fl-fbt-add]');
          this.label = this.querySelector('[data-fl-fbt-label]');
          this.error = this.querySelector('.fl-fbt__error');
          this.hero = this.querySelector('[data-hero]');
          this.idleLabel = this.label.textContent;
          this.addEventListener('change', () => this.refresh());
          this.button.addEventListener('click', () => this.addAll());
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              if (data?.sectionId !== this.section || !data.variant) return;
              this.hero.dataset.price = data.variant.price;
              this.querySelector('[data-fl-fbt-hero-price]').textContent = this.money(data.variant.price);
              this.refresh();
            });
          }
          this.refresh();
        }
        disconnectedCallback() {
          this.unsubscribe?.();
        }
        money(cents) {
          const currency = window.Shopify?.currency?.active || 'AUD';
          return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(cents / 100);
        }
        price(input) {
          const select = input.closest('.fl-fbt__item').querySelector('.fl-fbt__variant');
          const option = select?.selectedOptions[0];
          return parseInt(option ? option.dataset.price : input.dataset.price, 10) || 0;
        }
        checked() {
          return Array.from(this.querySelectorAll('.fl-fbt__check')).filter((input) => input.checked);
        }
        refresh() {
          const picked = this.checked();
          const total = picked.reduce((sum, input) => sum + this.price(input), 0);
          this.querySelector('[data-fl-fbt-total]').textContent = this.money(total);
          this.querySelector('[data-fl-fbt-count]').textContent = picked.length === 1 ? '1 item' : `${picked.length} items`;
          this.label.textContent = picked.length > 1 ? `Add all ${picked.length} to cart` : this.idleLabel;
          this.button.disabled = picked.length < 2;
        }
        addAll() {
          const heroButton = document.getElementById(`ProductSubmitButton-${this.section}`);
          const heroId = document.querySelector(`#product-form-${this.section} [name="id"]`)?.value;
          const items = [];
          if (heroId && !heroButton?.disabled) items.push({ id: Number(heroId), quantity: 1 });
          this.checked()
            .filter((input) => !input.hasAttribute('data-hero'))
            .forEach((input) => {
              const select = input.closest('.fl-fbt__item').querySelector('.fl-fbt__variant');
              items.push({ id: Number(select ? select.value : input.dataset.variant), quantity: 1 });
            });
          if (!items.length) return;

          const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          const body = { items };
          if (cart) {
            body.sections = cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            cart.setActiveElement?.(this.button);
          }

          this.button.disabled = true;
          this.button.classList.add('loading');
          this.button.querySelector('.loading__spinner')?.classList.remove('hidden');
          this.error.hidden = true;

          fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          })
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                this.error.textContent = response.description || response.message || 'Sorry, we could not add these.';
                this.error.hidden = false;
                return;
              }
              response.id = response.items?.[0]?.id;
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fl-bought-together', cartData: response });
              }
              if (cart && response.sections) {
                cart.classList.contains('is-empty') && cart.classList.remove('is-empty');
                cart.renderContents(response);
              } else {
                window.location = window.routes?.cart_url || '/cart';
              }
            })
            .catch(() => {
              this.error.textContent = 'Sorry, we could not add these. Please try again.';
              this.error.hidden = false;
            })
            .finally(() => {
              this.button.classList.remove('loading');
              this.button.querySelector('.loading__spinner')?.classList.add('hidden');
              this.refresh();
            });
        }
      }
    );
  }

  /* ---------- Product / tile carousel ---------- */
  if (!customElements.get('fl-carousel')) {
    customElements.define(
      'fl-carousel',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-fl-track]');
          this.prev = this.querySelector('[data-fl-prev]');
          this.next = this.querySelector('[data-fl-next]');
          if (!this.track) return;
          const step = (dir) => {
            const amount = Math.max(this.track.clientWidth * 0.85, 200);
            this.track.scrollBy({ left: dir * amount, behavior: reduceMotion ? 'auto' : 'smooth' });
          };
          this.prev?.addEventListener('click', () => step(-1));
          this.next?.addEventListener('click', () => step(1));
          this.track.addEventListener('scroll', () => this.update(), { passive: true });
          window.addEventListener('resize', () => this.update());
          this.update();
        }
        update() {
          const max = this.track.scrollWidth - this.track.clientWidth - 2;
          if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
          if (this.next) this.next.disabled = this.track.scrollLeft >= max;
          this.classList.toggle('is-static', max <= 0);
        }
      }
    );
  }

  /* ---------- Hero slider ---------- */
  if (!customElements.get('fl-slider')) {
    customElements.define(
      'fl-slider',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-fl-track]');
          this.slides = Array.from(this.querySelectorAll('[data-fl-slide]'));
          this.dots = Array.from(this.querySelectorAll('[data-fl-dot]'));
          if (!this.track || this.slides.length < 2) return;
          this.index = 0;
          this.querySelector('[data-fl-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
          this.querySelector('[data-fl-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
          this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
          this.track.addEventListener(
            'scroll',
            () => {
              clearTimeout(this.scrollTimer);
              this.scrollTimer = setTimeout(() => {
                const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
                this.setActive(i);
              }, 60);
            },
            { passive: true }
          );
          ['pointerdown', 'focusin', 'mouseenter'].forEach((e) => this.addEventListener(e, () => this.stop()));
          this.addEventListener('mouseleave', () => this.start());
          this.setActive(0);
          this.start();
        }
        go(i, user) {
          const n = this.slides.length;
          const target = (i + n) % n;
          this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
          this.setActive(target);
          if (user) this.stop();
        }
        setActive(i) {
          this.index = i;
          this.dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
          this.slides.forEach((s, k) => s.setAttribute('aria-hidden', String(k !== i)));
          this.slides.forEach((s, k) =>
            s.querySelectorAll('a, button').forEach((el) => (el.tabIndex = k === i ? 0 : -1))
          );
        }
        start() {
          const speed = parseInt(this.dataset.autoplay || '0', 10);
          if (!speed || reduceMotion) return;
          this.stop();
          this.timer = setInterval(() => this.go(this.index + 1), speed * 1000);
        }
        stop() {
          clearInterval(this.timer);
        }
      }
    );
  }

  /* ---------- Offer popup (welcome code) ---------- */
  if (!customElements.get('fl-offer-popup')) {
    const OFFER_KEY = 'fl_offer_hidden_until';
    const readHiddenUntil = () => {
      try {
        return parseInt(localStorage.getItem(OFFER_KEY) || '0', 10);
      } catch (e) {
        return 0;
      }
    };
    const hideForDays = (days) => {
      try {
        localStorage.setItem(OFFER_KEY, String(Date.now() + days * 864e5));
      } catch (e) {}
    };
    const copyText = (text) => {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
      }
      return fallbackCopy(text);
    };
    const fallbackCopy = (text) =>
      new Promise((resolve, reject) => {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        document.body.appendChild(area);
        area.select();
        area.setSelectionRange(0, text.length);
        let ok = false;
        try {
          ok = document.execCommand('copy');
        } catch (e) {}
        area.remove();
        ok ? resolve() : reject();
      });

    customElements.define(
      'fl-offer-popup',
      class extends HTMLElement {
        connectedCallback() {
          this.code = this.dataset.code || '';
          this.days = parseInt(this.dataset.days || '7', 10);
          this.copyButton = this.querySelector('[data-fl-offer-copy]');
          this.copyLabel = this.querySelector('[data-fl-offer-copy-label]');
          this.status = this.querySelector('[data-fl-offer-status]');
          this.defaultLabel = this.copyLabel ? this.copyLabel.textContent : '';
          this.copied = false;

          this.querySelector('[data-fl-offer-close]').addEventListener('click', () => this.close());
          if (this.copyButton) this.copyButton.addEventListener('click', () => this.copy());
          this.onKeyup = (e) => {
            if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
          };
          document.addEventListener('keyup', this.onKeyup);

          if (this.hasAttribute('data-design-mode')) {
            this.open();
            return;
          }
          if (readHiddenUntil() > Date.now()) return;
          // Shopping pages (product + collection) get the longer delay so ad visitors can browse first.
          const shopping = ['fl-tpl-product', 'fl-tpl-collection'].some((c) => document.body.classList.contains(c));
          const delaySetting = shopping && this.dataset.delayProduct
            ? this.dataset.delayProduct
            : this.dataset.delay;
          const delay = Math.max(0, parseInt(delaySetting || '6', 10)) * 1000;
          this.timer = setTimeout(() => this.open(), delay);
        }
        disconnectedCallback() {
          clearTimeout(this.timer);
          clearTimeout(this.resetTimer);
          document.removeEventListener('keyup', this.onKeyup);
        }
        open() {
          this.classList.add('is-open');
        }
        close() {
          this.classList.remove('is-open');
          if (!this.hasAttribute('data-design-mode')) hideForDays(this.copied ? Math.max(this.days, 30) : this.days);
        }
        copy() {
          copyText(this.code)
            .then(() => {
              this.copied = true;
              this.copyButton.classList.add('is-copied');
              if (this.copyLabel) this.copyLabel.textContent = this.dataset.copiedLabel || 'Copied!';
              if (this.status) this.status.textContent = `Code ${this.code} copied`;
              clearTimeout(this.resetTimer);
              this.resetTimer = setTimeout(() => {
                this.copyButton.classList.remove('is-copied');
                if (this.copyLabel) this.copyLabel.textContent = this.defaultLabel;
              }, 2500);
            })
            .catch(() => {
              /* Clipboard blocked: select the code so the shopper can copy it by hand */
              const codeEl = this.querySelector('[data-fl-offer-code]');
              if (!codeEl) return;
              const range = document.createRange();
              range.selectNodeContents(codeEl);
              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);
            });
        }
      }
    );
  }

  document.addEventListener('shopify:section:select', (e) => {
    const popup = e.target.querySelector('fl-offer-popup');
    if (popup) popup.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const popup = e.target.querySelector('fl-offer-popup');
    if (popup) popup.classList.remove('is-open');
  });

  /* ---------- Delivery estimate ---------- */
  function addBusinessDays(date, days) {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  }
  function renderEta(root = document) {
    const fmt = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    root.querySelectorAll('[data-fl-eta]').forEach((el) => {
      const min = parseInt(el.dataset.min || '5', 10);
      const max = parseInt(el.dataset.max || '6', 10);
      const template = el.dataset.template || '';
      if (!template) return;
      const now = new Date();
      el.textContent = template
        .replace('{start}', fmt.format(addBusinessDays(now, min)))
        .replace('{end}', fmt.format(addBusinessDays(now, max)));
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => renderEta());
  } else {
    renderEta();
  }
  document.addEventListener('shopify:section:load', (e) => renderEta(e.target));
})();
